/**
 * Utilidades de validación y sanitización de datos
 */

// Validación de email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validación de contraseña (min 6 caracteres, al menos una letra y un número)
export const isValidPassword = (password) => {
  return password && password.length >= 6;
};

// Validación de contraseña fuerte (opcional)
export const isStrongPassword = (password) => {
  const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
  return strongRegex.test(password);
};

// Validación de RUT chileno
export const isValidRUT = (rut) => {
  if (!rut || typeof rut !== 'string') return false;
  
  // Remover puntos y guión
  const cleanRUT = rut.replace(/\./g, '').replace(/-/g, '');
  
  // Verificar formato
  if (!/^\d{7,8}[0-9Kk]$/.test(cleanRUT)) return false;
  
  const body = cleanRUT.slice(0, -1);
  const dv = cleanRUT.slice(-1).toUpperCase();
  
  // Calcular dígito verificador
  let suma = 0;
  let multiplo = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    suma += multiplo * parseInt(body[i]);
    multiplo = multiplo < 7 ? multiplo + 1 : 2;
  }
  
  const dvEsperado = 11 - (suma % 11);
  const dvCalculado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
  
  return dv === dvCalculado;
};

// Formatear RUT
export const formatRUT = (rut) => {
  if (!rut) return '';
  
  // Remover todo excepto números y K
  const cleanRUT = rut.replace(/[^0-9Kk]/g, '').toUpperCase();
  
  if (cleanRUT.length <= 1) return cleanRUT;
  
  const body = cleanRUT.slice(0, -1);
  const dv = cleanRUT.slice(-1);
  
  // Formatear con puntos
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${formattedBody}-${dv}`;
};

// Validación de teléfono chileno
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length >= 8 && cleanPhone.length <= 12;
};

// Formatear teléfono
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length === 9) {
    // Formato: +56 9 1234 5678
    return `+56 ${cleanPhone.slice(0, 1)} ${cleanPhone.slice(1, 5)} ${cleanPhone.slice(5)}`;
  }
  
  return phone;
};

// Sanitizar string (remover caracteres peligrosos)
export const sanitizeString = (str) => {
  if (!str) return '';
  
  // Remover scripts y tags HTML
  let sanitized = str.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  // Remover caracteres especiales peligrosos para SQL
  sanitized = sanitized.replace(/['"`;\\]/g, '');
  
  return sanitized.trim();
};

// Sanitizar HTML (mantener solo tags seguros)
export const sanitizeHTML = (html) => {
  if (!html) return '';
  
  const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'p', 'br'];
  const div = document.createElement('div');
  div.innerHTML = html;
  
  const walk = (node) => {
    if (node.nodeType === 3) return; // text node
    
    if (node.nodeType === 1) { // element node
      if (!allowedTags.includes(node.tagName.toLowerCase())) {
        node.replaceWith(...node.childNodes);
      }
    }
    
    Array.from(node.childNodes).forEach(walk);
  };
  
  walk(div);
  return div.innerHTML;
};

// Validar número positivo
export const isPositiveNumber = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

// Validar monto en pesos chilenos
export const isValidAmount = (amount) => {
  const num = parseFloat(amount);
  return !isNaN(num) && num >= 0 && num <= 999999999;
};

// Formatear monto en pesos chilenos
export const formatCurrency = (amount) => {
  if (isNaN(amount)) return '$0';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

// Validar fecha
export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

// Validar que la fecha no sea futura
export const isNotFutureDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date <= today;
};

// Validar campo requerido
export const isRequired = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined && value !== '';
};

// Validar longitud mínima
export const minLength = (value, min) => {
  if (typeof value === 'string') {
    return value.trim().length >= min;
  }
  return false;
};

// Validar longitud máxima
export const maxLength = (value, max) => {
  if (typeof value === 'string') {
    return value.trim().length <= max;
  }
  return false;
};

// Validar rango numérico
export const inRange = (value, min, max) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= min && num <= max;
};

// Mensajes de error estandarizados
export const errorMessages = {
  required: 'Este campo es obligatorio',
  email: 'Ingrese un correo electrónico válido',
  password: 'La contraseña debe tener al menos 6 caracteres',
  passwordStrength: 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial',
  passwordMatch: 'Las contraseñas no coinciden',
  rut: 'RUT inválido',
  phone: 'Número de teléfono inválido',
  amount: 'Monto inválido',
  date: 'Fecha inválida',
  futureDate: 'La fecha no puede ser futura',
  minLength: (min) => `Debe tener al menos ${min} caracteres`,
  maxLength: (max) => `No puede exceder ${max} caracteres`,
  range: (min, max) => `Debe estar entre ${min} y ${max}`,
  positive: 'Debe ser un número positivo',
  
  // Errores de sistema
  networkError: 'Error de conexión. Por favor, intente nuevamente.',
  serverError: 'Error del servidor. Por favor, intente más tarde.',
  unauthorized: 'No autorizado. Por favor, inicie sesión nuevamente.',
  forbidden: 'No tiene permisos para realizar esta acción.',
  notFound: 'Recurso no encontrado.',
  conflict: 'Ya existe un registro con estos datos.',
  
  // Errores de login
  invalidCredentials: 'Correo o contraseña incorrecta',
  accountPending: 'Su cuenta está pendiente de aprobación',
  accountExpired: 'Su cuenta ha expirado. Contacte al administrador.',
  accountInactive: 'Su cuenta está inactiva. Contacte al administrador.',
  
  // Errores de registro
  emailExists: 'Este correo ya está registrado',
  rutExists: 'Este RUT ya está registrado',
  
  // Mensajes de éxito
  loginSuccess: 'Inicio de sesión exitoso',
  registerSuccess: 'Registro exitoso. Su cuenta está pendiente de aprobación.',
  saveSuccess: 'Guardado correctamente',
  deleteSuccess: 'Eliminado correctamente',
  updateSuccess: 'Actualizado correctamente'
};

// Validador de formulario completo
export const validateForm = (formData, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const value = formData[field];
    const fieldRules = rules[field];
    
    // Verificar requerido
    if (fieldRules.required && !isRequired(value)) {
      errors[field] = errorMessages.required;
      return;
    }
    
    // Si el campo está vacío y no es requerido, saltar otras validaciones
    if (!isRequired(value)) return;
    
    // Validación de email
    if (fieldRules.email && !isValidEmail(value)) {
      errors[field] = errorMessages.email;
      return;
    }
    
    // Validación de RUT
    if (fieldRules.rut && !isValidRUT(value)) {
      errors[field] = errorMessages.rut;
      return;
    }
    
    // Validación de teléfono
    if (fieldRules.phone && !isValidPhone(value)) {
      errors[field] = errorMessages.phone;
      return;
    }
    
    // Validación de longitud mínima
    if (fieldRules.minLength && !minLength(value, fieldRules.minLength)) {
      errors[field] = errorMessages.minLength(fieldRules.minLength);
      return;
    }
    
    // Validación de longitud máxima
    if (fieldRules.maxLength && !maxLength(value, fieldRules.maxLength)) {
      errors[field] = errorMessages.maxLength(fieldRules.maxLength);
      return;
    }
    
    // Validación de monto
    if (fieldRules.amount && !isValidAmount(value)) {
      errors[field] = errorMessages.amount;
      return;
    }
    
    // Validación de fecha
    if (fieldRules.date && !isValidDate(value)) {
      errors[field] = errorMessages.date;
      return;
    }
    
    // Validación personalizada
    if (fieldRules.custom && !fieldRules.custom(value, formData)) {
      errors[field] = fieldRules.customMessage || 'Valor inválido';
      return;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
